import { PaginatedEvent } from "@models/pagination_event";
import { BehaviorSubject, Observable, of as observableOf } from "rxjs";
import { deepCopy } from "@helpers/utils";
import queryString from "query-string";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { AssetScroll } from "@models/asset_scroll";
import { catchError, map } from "rxjs/operators";
import { ErrorClass } from "@models";
import { Helper } from "@helpers/functions";
import { PRICES_SCROLL_PER_PAGE } from "@helpers/constants";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";

export abstract class PaginationService<T> {
	protected list: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
	private showScrollError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private lastEvent: PaginatedEvent = null;
	private endOfList: boolean = false;

	public isEndOfList$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	protected fetchError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public abstract convertPaginationList(list: AssetScroll[]): T[];

	protected constructor(
		protected readonly coreStoreFacade: CoreStoreFacade,
		public apiCallService: ApicallService,
		protected endpoint: string,
		protected apiVersion: "v1" | "v2" | "v3",
		// TODO: eslint fix
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		protected apiCallOption: any = null
	) {}

	public getApiData(e: PaginatedEvent): Observable<T[]> {
		const queryParams = queryString.stringify(e, {
			arrayFormat: "bracket",
			skipEmptyString: true,
			skipNull: true
		});
		queryParams.toLowerCase();
		const apiEndpoint = `${this.endpoint}?${queryParams}`;

		this.coreStoreFacade.updateOfflineState(false);
		const option = new ApicallOption(this.apiCallOption);
		option.apiVersion = this.apiVersion;
		return this.apiCallService.get(apiEndpoint, option).pipe(
			map((assets: AssetScroll[]) => this.convertPaginationList(assets)), // Return actual tradable based on id comparison
			catchError((error: Error) => {
				this.fetchError.next(true);
				if (error instanceof ErrorClass) {
					return observableOf(null);
				}
				Helper.handleError(error);
				return observableOf([]);
			})
		);
	}

	public fetchApiData(event: PaginatedEvent): void {
		if (this.endOfList) {
			return;
		}
		this.lastEvent = PaginationService.deepCopyScrollEvents(event);
		this.isLoading.next(true);
		this.getApiData(event).subscribe((list: []) => {
			this.endOfList = list && list.length === 0;
			this.isEndOfList$.next(this.endOfList);
			// Error
			if (!list) {
				const errList = event.getNextPageRequired() ? [...this.list.getValue()] : [];
				this.list.next(errList);
				if (!this.endOfList) {
					this.showScrollError.next(true);
					this.isLoading.next(false);
				}
				return;
			}
			// Update list
			this.showScrollError.next(false);
			const updateList = event.getNextPageRequired() ? [...this.list.getValue(), ...list] : list;
			this.list.next(updateList);
			this.isLoading.next(false);
		});
	}

	public static deepCopyScrollEvents(event: PaginatedEvent): PaginatedEvent {
		return deepCopy(event, PaginatedEvent);
	}

	public getListObservable(): Observable<T[]> {
		return this.list.asObservable();
	}

	public getListValue(): T[] {
		return this.list.getValue();
	}

	public resetList(): void {
		this.list.next([]);
	}

	public getShowScrollErrorObservable(): Observable<boolean> {
		return this.showScrollError.asObservable();
	}

	public getLastEvent(): PaginatedEvent {
		return this.lastEvent;
	}

	public getLoadingObservable(): Observable<boolean> {
		return this.isLoading.asObservable();
	}

	public getFetchErrorObservable(): Observable<boolean> {
		return this.fetchError.asObservable();
	}

	public getErrorValue(): boolean {
		return this.fetchError.getValue();
	}

	public clearEndOfList(): void {
		this.endOfList = false;
		this.isEndOfList$.next(this.endOfList);
	}

	public resetError(): void {
		this.fetchError.next(false);
	}

	/*
		Used for Change Detection onPush - When an item inside the list mutates its values (eg: asset price changes).
		The main list doesn't trigger new change, so needs manual re-trigger with same list (only attributes inside child changed).
	*/
	public refreshList(): void {
		this.list.next([...this.list.getValue()]);
	}

	// ------------------------ UTILITY METHODS FOR PAGE CONTROL AND DATA FETCH

	public getNewEvent(): PaginatedEvent {
		return new PaginatedEvent();
	}

	public initList(pageSize = PRICES_SCROLL_PER_PAGE, filters: string[] = ["all"]): void {
		const event = this.getNewEvent();
		event.setType(filters);
		event.setPageSize(pageSize);
		this.fetchListData(event);
	}

	public fetchListUpdate(pageSize = PRICES_SCROLL_PER_PAGE): void {
		const fetchEvent: PaginatedEvent = this.getLastEvent();
		fetchEvent.setPageSize(pageSize);
		fetchEvent.setPage(fetchEvent.getPage() + 1);
		this.fetchListData(fetchEvent);
	}

	public searchListUpdate(searchQuery: string = "", pageSize = PRICES_SCROLL_PER_PAGE): void {
		this.clearEndOfList();
		const searchEvent: PaginatedEvent = this.getNewEvent();
		searchEvent.setType(this.getLastEvent().getType());
		searchEvent.setPageSize(pageSize);
		searchEvent.setSearchQuery(searchQuery);
		this.fetchListData(searchEvent);
	}

	public filterUpdate(filters: string[] = [""], searchQuery: string = "", pageSize = PRICES_SCROLL_PER_PAGE): void {
		this.clearEndOfList();
		const searchEvent: PaginatedEvent = this.getNewEvent();
		searchEvent.setPageSize(pageSize);
		searchEvent.setType(this.getFilterTag(filters.map((x) => x.toLowerCase())));
		searchEvent.setSearchQuery(searchQuery);
		this.fetchListData(searchEvent);
	}

	private getFilterTag(filters: string[]): string[] {
		if (filters.length === 1) {
			const filter = filters[0];
			if (filter === "crypto") {
				return ["cryptocoin"];
			} else if (filter === "currency" || filter === "") {
				return ["all"];
			}

			return filters;
		}

		return filters;
	}

	private fetchListData(event: PaginatedEvent): void {
		this.fetchApiData(event);
	}
}
